import { ChatContainer, type ChatHistoryItemType } from '@/components/chat';
import {
  type AiMessage,
  type Message,
  MessageType,
  type PrinceAttachment,
} from '@/features/prince/messages/types';
import { stripXmlBlocks } from '@/features/prince/messages/utils/stripXmlBlocks';
import { useAuth } from '@/hooks/useAuth';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  StreamRequestType,
  chatHistoryInfiniteQueryOptions,
  conversationsQueryOptions,
  useDeleteConversation,
  useMessageRatingMutation,
  useToggleFavorite,
} from '@/api/queries/prince';
import { FeatureFlag } from '@/common';
import { ChatMessage } from '@/features/prince/messages/ChatMessage';
import {
  type Agent,
  EXTERNAL_AGENTS,
  EXTERNAL_MODELS,
  type Model,
  isValidAgent,
  isValidModel,
} from '@/features/prince/models';
import { PrinceChatHeader } from '@/features/prince/PrinceChatHeader';
import { PrinceChatInput, type PrinceChatInputRef } from '@/features/prince/PrinceChatInput';
import { ChatHistoryItem } from '@/features/prince/sidebar/ChatHistoryItem';
import { streamMessage } from '@/features/prince/stream-message-utils';
import { WelcomeMessage } from '@/features/prince/WelcomeMessage';
import { useConversationId } from '@/hooks/useConversationId';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { usePrevious } from '@/hooks/usePrevious';
import { captureException as captureSentryException } from '@sentry/react';

const getNewMessageId = () => crypto.randomUUID();
const getNewRunId = () => crypto.randomUUID();

enum AbortStreamingReason {
  USER_MANUAL_REQUEST = 'user_manual_request',
}

type StreamingState =
  | {
      isStreaming: true;
      abortController: AbortController;
    }
  | {
      isStreaming: false;
      abortController: null;
    };

export default function PrincePage() {
  const { agent: agentParam, model: modelParam } = useParams<{
    agent: string;
    model: string;
  }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('prince');
  const { currentConversationId, handleNewConversation, setConversationId } = useConversationId({
    generateIfNotDefined: true,
  });
  const queryClient = useQueryClient();

  const {
    enabled: [isScoutInternalAgentsEnabled, isScoutInternalModelsEnabled],
  } = useIsFeatureFlagEnabled([
    FeatureFlag.SCOUT_INTERNAL_AGENTS,
    FeatureFlag.SCOUT_INTERNAL_MODELS,
  ]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [hasActiveSessionMessages, setHasActiveSessionMessages] = useState(false);
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    abortController: null,
  });
  const [lastUserMessageTimestamp, setLastUserMessageTimestamp] = useState<number>(0);
  const chatInputRef = useRef<PrinceChatInputRef>(null);

  // Fetch user conversations
  const { data: conversations = [], isLoading: isLoadingConversations } = useQuery(
    conversationsQueryOptions()
  );

  // Check if the current conversation exists in the backend
  // Temporary conversations (starting with "temp-") don't exist in backend yet
  const conversationExists =
    currentConversationId &&
    !currentConversationId.startsWith('temp-') &&
    conversations.some((conv: { id: string }) => conv.id === currentConversationId);

  // Fetch chat history messages for the current conversation using infinite query
  const {
    data: chatHistoryData,
    isLoading: isLoadingChatHistory,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    chatHistoryInfiniteQueryOptions(currentConversationId || '', conversationExists || false)
  );

  // Flatten all pages into a single messages array
  // Pages are in reverse chronological order (newest first), so reverse them
  const chatHistoryMessages = useMemo(() => {
    if (!chatHistoryData?.pages) return [];
    return [...chatHistoryData.pages].reverse().flatMap(page => page.messages);
  }, [chatHistoryData]);

  const currentChatHistoryAgentId = chatHistoryData?.pages?.[0]?.conversation?.agent_id;

  // Load messages when chat history changes
  useEffect(() => {
    if (!hasActiveSessionMessages) {
      setMessages(chatHistoryMessages);
    }
  }, [chatHistoryMessages, hasActiveSessionMessages]);
  const previousChatHistoryAgentId = usePrevious(currentChatHistoryAgentId);

  useEffect(() => {
    if (
      currentChatHistoryAgentId &&
      currentChatHistoryAgentId !== previousChatHistoryAgentId &&
      currentChatHistoryAgentId !== agentParam
    ) {
      // Handle agent mismatch by redirecting to correct URL (only when conversation agent changes)
      const searchParams = new URLSearchParams(window.location.search);
      navigate(`/prince/${currentChatHistoryAgentId}/${modelParam}?${searchParams.toString()}`);
    }
  }, [currentChatHistoryAgentId, previousChatHistoryAgentId, agentParam, modelParam, navigate]);

  // Mutations for delete and favorite functionality
  const deleteConversationMutation = useDeleteConversation();
  const toggleFavoriteMutation = useToggleFavorite();
  const messageRatingMutation = useMessageRatingMutation();

  // Map conversations to the format expected by ChatContainer
  const chatHistory: Array<ChatHistoryItemType & { isFavorite?: boolean }> = conversations.map(
    (conv: { id: string; title: string; isFavorite?: boolean }) => ({
      id: conv.id,
      title: conv.title,
      isActive: conv.id === currentConversationId,
      isFavorite: conv.isFavorite,
    })
  );

  if (!user) return null;
  if (
    !agentParam ||
    !modelParam ||
    !isValidAgent(agentParam) ||
    !isValidModel(modelParam) ||
    (!isScoutInternalModelsEnabled && !EXTERNAL_MODELS.includes(modelParam as Model)) ||
    (!isScoutInternalAgentsEnabled && !EXTERNAL_AGENTS.includes(agentParam as Agent))
  ) {
    return (
      <div className="p-4 h-full">
        <h1 className="text-xl font-bold mb-4">{t('errors.invalidAgentOrModel')}</h1>
        <p className="text-sm text-gray-600 mt-2">{t('errors.noAgentOrModelNavigation')}</p>
      </div>
    );
  }

  const agent = agentParam as Agent;
  const model = modelParam as Model;

  const handleSendMessage = async (message: string) => {
    if (!currentConversationId) return;

    // Trigger auto-scroll re-enabling with current timestamp
    setLastUserMessageTimestamp(Date.now());

    const newAbortController = new AbortController();
    setStreamingState({
      isStreaming: true,
      abortController: newAbortController,
    });

    // Check if this is the first message in the conversation (conversation creation)
    const isFirstMessage = messages.length === 0;

    // Store the display version (without XML blocks) in frontend state
    // but send the full version (with XML blocks) to the backend
    const userMessage: Message = {
      id: getNewMessageId(),
      type: MessageType.USER,
      content: message,
      timestamp: new Date().toISOString(),
      // TODO: use the attachments from the backend
      // https://sitetraker.atlassian.net/browse/SIT-52603
      attachments: [],
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setHasActiveSessionMessages(true);

    await streamMessage(
      {
        type: StreamRequestType.NewUserMessage,
        message,
        run_id: getNewRunId(),
      },
      agent,
      model,
      currentConversationId,
      {
        onAIResponseStart: newMessage => {
          setMessages(prev => [...prev, newMessage]);
        },
        onAIResponseUpdates: updatedMessage => {
          setMessages(prev =>
            prev.map(msg => (msg.id === updatedMessage.id ? updatedMessage : msg))
          );
        },
        onAIResponseComplete: finalMessage => {
          setMessages(prev => prev.map(msg => (msg.id === finalMessage.id ? finalMessage : msg)));
          setStreamingState({
            isStreaming: false,
            abortController: null,
          });
          if (isFirstMessage) {
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
          }
          // Invalidate chat history cache to ensure backend persistence is reflected
          queryClient.invalidateQueries({
            queryKey: ['conversations', currentConversationId, 'messages', 'infinite'],
          });
        },
        onError: error => {
          toast.error(t('errors.sendMessageFailed', { message: error }));
          setStreamingState({
            isStreaming: false,
            abortController: null,
          });
        },
      },
      newAbortController
    );
  };

  const handleRegenerateLastMessage = async () => {
    if (!currentConversationId || messages.length === 0) return;

    const newAbortController = new AbortController();
    setStreamingState({
      isStreaming: true,
      abortController: newAbortController,
    });

    // Remove the last AI message and any messages after it
    const messagesUpToLastAI = messages.slice(0, -1);
    setMessages(messagesUpToLastAI);

    await streamMessage(
      {
        type: StreamRequestType.RegenerateAgentResponse,
        run_id: getNewRunId(),
        run_id_to_regenerate: (messages[messages.length - 1] as AiMessage).agentRunId,
      },
      agent,
      model,
      currentConversationId,
      {
        onAIResponseStart: newMessage => {
          setMessages(prev => [...prev, newMessage]);
        },
        onAIResponseUpdates: updatedMessage => {
          setMessages(prev =>
            prev.map(msg => (msg.id === updatedMessage.id ? updatedMessage : msg))
          );
        },
        onAIResponseComplete: finalMessage => {
          setMessages(prev => prev.map(msg => (msg.id === finalMessage.id ? finalMessage : msg)));
          setStreamingState({
            isStreaming: false,
            abortController: null,
          });
        },
        onError: () => {
          toast.error(t('errors.regenerateFailed'));
          setStreamingState({
            isStreaming: false,
            abortController: null,
          });
        },
      },
      newAbortController
    );
  };

  const handleAutoContinuation = async (sourceMessageId: string) => {
    if (!currentConversationId || streamingState.isStreaming) return;

    setMessages(prev =>
      prev.map(msg =>
        msg.id === sourceMessageId && msg.type === MessageType.AI_AGENT && msg.autoContinuation
          ? {
              ...msg,
              autoContinuation: {
                ...msg.autoContinuation,
                requiresUserConfirmation: false,
              },
            }
          : msg
      )
    );

    await handleSendMessage('continue');
  };

  const handleAbortAgentStream = () => {
    if (streamingState.abortController) {
      streamingState.abortController.abort(AbortStreamingReason.USER_MANUAL_REQUEST);

      // Mark the current streaming message as aborted
      setMessages(prev =>
        prev.map(msg =>
          msg.type === MessageType.AI_AGENT && msg.isStreaming
            ? { ...msg, isStreaming: false, isAborted: true }
            : msg
        )
      );

      setStreamingState({
        isStreaming: false,
        abortController: null,
      });
    }
  };

  const resetChatState = () => {
    setMessages([]);
    setHasActiveSessionMessages(false);
    const newConversationId = handleNewConversation();
    chatInputRef.current?.clear();
    handleAbortAgentStream();

    return { newConversationId };
  };

  const handleSelectChat = async (conversationId: string) => {
    try {
      // Reset the active session flag when navigating to a different conversation
      setHasActiveSessionMessages(false);
      setIsLoadingMessages(true);
      handleAbortAgentStream();
      // Set the conversation ID first to trigger React Query cache lookup
      setConversationId(conversationId);

      // Invalidate and refetch the chat history query to ensure fresh data
      await queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'messages', 'infinite'],
      });

      chatInputRef.current?.clear();
    } catch (error) {
      toast.error(t('errors.loadChatHistoryFailed'));
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      await deleteConversationMutation.mutateAsync(conversationId);

      // If the deleted conversation was the current one, clear messages and create a new chat
      if (conversationId === currentConversationId) {
        setMessages([]);
        resetChatState();
      }

      // Invalidate conversations cache when deleting a conversation
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      toast.success(t('toasts.conversationDeletedSuccessfully'));
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error(t('toasts.failedToDeleteConversation'));
    }
  };

  const handleToggleFavorite = async (conversationId: string) => {
    try {
      await toggleFavoriteMutation.mutateAsync(conversationId);
      // Invalidate conversations cache when toggling favorite status
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error(t('toasts.failedToToggleFavorite'));
    }
  };

  const handleRating = async (message: AiMessage, score: number, comment?: string) => {
    try {
      await messageRatingMutation.mutateAsync({
        runId: message.agentRunId,
        score,
        comment,
      });

      toast.success(t('messages.rating.success'));
      setMessages(prev =>
        prev.map(msg =>
          msg.id === message.id && msg.type === MessageType.AI_AGENT
            ? { ...msg, userRating: { score } }
            : msg
        )
      );
    } catch (error) {
      // Only capture errors
      captureSentryException(error);
    }
  };

  const handleAttachment = () => {
    console.log('Attachment functionality not yet implemented');
    // TODO: Implement file attachment when needed
    // https://sitetraker.atlassian.net/browse/SIT-52603
  };

  const handleAttachmentClick = (attachment: PrinceAttachment) => {
    console.log('Attachment clicked:', attachment.id, attachment.name);
    // TODO: Implement attachment viewing when needed
    // https://sitetraker.atlassian.net/browse/SIT-52603
  };

  const handleCopy = async (message: Message) => {
    const content =
      message.type === MessageType.USER
        ? stripXmlBlocks(message.content)
        : message.content.filter(part => typeof part === 'string').join('');

    await navigator.clipboard.writeText(content);
  };

  const renderMessage = (message: Message, isLastMessage: boolean) => {
    const showFeedbackActions =
      isLastMessage && message.type === MessageType.AI_AGENT && !message.isStreaming;
    return (
      <ChatMessage
        key={message.id}
        message={message}
        onAttachmentClick={handleAttachmentClick}
        onThumbsUp={showFeedbackActions ? () => handleRating(message, 1) : undefined}
        onThumbsDown={
          showFeedbackActions ? (comment?: string) => handleRating(message, 0, comment) : undefined
        }
        onRefresh={showFeedbackActions ? handleRegenerateLastMessage : undefined}
        onCopy={showFeedbackActions ? () => handleCopy(message) : undefined}
        onContinueAuto={
          message.type === MessageType.AI_AGENT && message.autoContinuation?.limitReached
            ? () => handleAutoContinuation(message.id)
            : undefined
        }
      />
    );
  };

  const renderHistoryItem = (conversation: ChatHistoryItemType & { isFavorite?: boolean }) => {
    return (
      <ChatHistoryItem
        key={conversation.id}
        id={conversation.id}
        title={conversation.title}
        isActive={conversation.isActive}
        isFavorite={conversation.isFavorite}
        onClick={() => handleSelectChat(conversation.id)}
        onDelete={handleDeleteConversation}
        onToggleFavorite={handleToggleFavorite}
      />
    );
  };

  return (
    <div className="h-full w-full">
      <ChatContainer
        conversationsHeader={
          <PrinceChatHeader
            onAgentChange={resetChatState}
            isScoutInternalAgentsEnabled={isScoutInternalAgentsEnabled}
            isScoutInternalModelsEnabled={isScoutInternalModelsEnabled}
          />
        }
        welcomeMessage={
          messages.length === 0 && !isLoadingChatHistory && !isLoadingConversations ? (
            <WelcomeMessage />
          ) : undefined
        }
        currentConversationMessages={messages}
        conversations={chatHistory}
        renderMessage={renderMessage}
        renderHistoryItem={renderHistoryItem}
        onNewConversation={resetChatState}
        lastUserMessageTimestamp={lastUserMessageTimestamp}
        chatInput={
          <PrinceChatInput
            ref={chatInputRef}
            isStreaming={streamingState.isStreaming}
            onAbortStreaming={handleAbortAgentStream}
            onSend={handleSendMessage}
            onAttachment={handleAttachment}
            disabled={isLoadingConversations || isLoadingMessages || isLoadingChatHistory}
          />
        }
        isLoadingMessages={isLoadingConversations || isLoadingMessages || isLoadingChatHistory}
        onLoadMore={() => fetchNextPage()}
        hasMoreMessages={hasNextPage}
        isLoadingMore={isFetchingNextPage}
      />
    </div>
  );
}
